/**
 * 커스텀 이벤트 관리
 *
 * // ✅ 이벤트 등록
 * $j.Common.EventBus.on('namespace','user:login', (e, user) => {
 *   console.log('로그인 이벤트 발생:', user);
 * });
 *
 * // ✅ 한 번만 실행되는 이벤트
 * $j.Common.EventBus.once('namespace','data:init', (e, data) => {
 *   console.log('데이터 초기화:', data);
 * });
 *
 * // ✅ 이벤트 발생
 * $j.Common.EventBus.trigger('user:login', { id: 1, name: '홍길동' });
 * $j.Common.EventBus.trigger('data:init', { status: 'OK' });
 *
 * // ✅ 이벤트 해제
 * $j.Common.EventBus.offEvent('user:login'); ❌이벤트를 모두 제거하므로 비추
 * $j.Common.EventBus.offEvent('user:login','namespace'); ✅특정 이벤트만 제거
 * $j.Common.EventBus.offNamespace('namespace'); ✅네임스페이스에 기반한 이벤트를 모조리 제거 (모달이 제거되었을때)
 *
 * // ✅ 전체 이벤트 초기화
 * $j.Common.EventBus.clear();
 *
 * // ✅ 등록된 이벤트 목록 확인
 * $j.Common.EventBus.list();
 */
export default class EventBus {
    constructor() {
        this.bus = $({});
        this.debug = $j.Core.Config.isEventDebug;
        // this.debug = 1;
        // 원본 namespace → safe namespace 캐시(동일 입력이면 동일 결과)
        this._nsMap = new Map();
        /**
         * 모든 커스텀 이벤트 목록 (여기에 등록해야만 사용할수있다)
         * ❌. (쩜) 사용금지, 쩜은 namespace 구분자이므로
         * @type {string[]}
         */
        this.registry = [
            'adm:push-config:saved', // 관리자>푸시설정 저장시
            'app:share-image', // app 에서 이미지 공유가 완료되었을때
            'sms:auth', // sms 인증 이벤트
            'car-select:load', // 차량 로드 이벤트
            'car-select:type-change', // 차량 타입 변경 이벤트
            'kakao-map:select', // 좌표선택시
            'img-editor:saved', // 이미지 에디터 저장 이벤트
            'upload:image', // 이미지 업로드 완료
            'app:photo:preview', // 격자 사진 한 장 크게 ({token, url, w, h} / {token, error}) — $j.F.App.previewPhoto 콜백
            'app:phone:number', // 앱이 폰번호를 전달했을 때 ($j.F.App.getPhoneNumber 콜백)
            'app:device:context', // 앱이 디바이스 컨텍스트(GPS·주소·FCM·앱버전·모델·회원번호)를 전달했을 때 ($j.F.App.getDeviceContext 콜백)
            'app:speech:result', // STT 인식 결과 ({howinput, jobAct, word}) — 구 get_speechWord
            'app:social:result', // 소셜 인증 결과 ({res, token, sns_type}) — 구 WebView.applySocialInfo
            'app:location:permission', // GPS 권한 요청 결과 ({granted}) — $j.F.App.requestLocationPermission 콜백
            'app:photos', // 디바이스 사진 한 페이지 ({status, permission, items, cursor, hasMore, did?}) — $j.F.App.listPhotos 콜백
            'social:complete', // 소셜 인증 최종 분기 데이터 ({result, message, info, next}) — 앱(ajax 후) / popup(postMessage) 양쪽 경로 수렴점
            'app:devil:master', // 악덕알리미 '악덕업체보기' 딥링크 ({master_num}) — Sites/Devil/Bootstrap 이 마스터 상세 BS 표시
            'docs:main-hub:save', // 서류 저장 누를때
            'docs:main-hub:review', // 서류 심사요청 누를때
            'docs:main:cardriver', // 장비, 기사 추가 시 새로고침
            'docs:main:companies', // 업체 추가 시 새로고침
            'docs:img:clear', // 서류이미지 전체 삭제시
            'docs:old:selected', // 기존 서류에서 서류 누를때
            'docs:changed', // /m/docs/v1 UploadSheet — 업로드/삭제 후 페이지 hidden input·배지 동기화
            'docs:new-custom', // /m/docs/v1 UploadSheet — 커스텀(9999) 최초 업로드 시 새 박스 생성
            'docs:image-edited', // /m/docs/v1 UploadSheet — 이미지 편집기 저장 후 미리보기 부분 갱신
            'pop:apply', // 팝업 닫기 버튼 누를 때 적용
            'member:agree-ad', // 작업사진,노트 홍보활용 동의
            'docs-guide:driver-change-cancel', // 유형변경 철회
            'docs-guide:driver-change-confirm', // 유형변경 승인요청
            'zoom:close', // 줌 닫을 때
            'zoomphoto:delete', // ZoomPhoto 갤러리 모드에서 삭제 버튼 클릭 시
            'gallery:delete', // F.Order.Gallery 에서 사진 삭제 시 (ordernum, img_id)
            'order:jobphoto:changed', // 현장사진 커밋/삭제로 오더 사진목록이 바뀜 ({ordernum, tag, count}) — DevicePicker → DiaryJobs
            'BS:close', // BS 닫을 때
            'JBModal:close', // JBModal 닫을 때
            'share-history:back', // 공유내역에서 공유로 복귀
        ];
    }

    /* =========================
    * namespace utils
    * ========================= */

    /**
     * jQuery 네임스페이스에 안전한 문자열로 변환
     * - '.' 같은 문법 문자를 안전 문자로 치환
     * - 동일 입력은 동일 출력 (캐시)
     */
    _toSafeNamespace(namespace) {
        if (!namespace) return '';

        // 이미 캐시에 있으면 재사용
        if (this._nsMap.has(namespace)) return this._nsMap.get(namespace);

        // 1) 문자열화
        const raw = String(namespace);

        // 2) jQuery 네임스페이스에서 안전한 문자로 치환
        //    허용: a-z A-Z 0-9 _ -
        //    그 외는 모두 '-'로 변환 ('.', '$', ':', 공백 등 포함)
        let safe = raw
            .replace(/[^a-zA-Z0-9_-]/g, '-') // '.' 포함 모든 특수문자 치환
            .replace(/-+/g, '-')            // 연속 '-' 정리
            .replace(/^-|-$/g, '');         // 앞뒤 '-' 제거

        // 3) 비어버리면 fallback
        if (!safe) {
            this._nsSeq += 1;
            safe = `ns-${this._nsSeq}`;
        }

        // 4) 캐시
        this._nsMap.set(namespace, safe);

        return safe;
    }

    _withNamespace(event, namespace) {
        const ns = this._toSafeNamespace(namespace);
        return ns ? `${event}.${ns}` : event;
    }

    _assertRegistered(event) {
        if (!this.registry.includes(event)) {
            // debug 플래그와 무관하게 콘솔에 에러로 노출
            // 등록 누락 시 silent drop 되면 원인 추적이 어려우므로 항상 눈에 띄게 함
            console.error(`[EventBus] Unregistered event: "${event}" — registry 배열에 추가 필요 (resources/js/Common/EventBus.js)`);
            return false;
        }
        return true;
    }

    /* =========================
     * listen
     * ========================= */

    // ✅ on(namespace, event, handler)
    on(namespace, event, handler) {
        if (!namespace) {
            console.warn('[EventBus:on] namespace is required');
            return;
        }
        if (!this._assertRegistered(event)) return;

        const evt = this._withNamespace(event, namespace);
        if (this.debug) console.log('[EventBus:on]', evt, '(raw ns:', namespace, ')');

        this.bus.on(evt, handler);
    }

    once(namespace, event, handler) {
        if (!namespace) {
            console.warn('[EventBus:once] namespace is required');
            return;
        }
        if (!this._assertRegistered(event)) return;

        const evt = this._withNamespace(event, namespace);
        if (this.debug) console.log('[EventBus:once]', evt, '(raw ns:', namespace, ')');

        this.bus.one(evt, handler);
    }

    /* =========================
     * trigger
     * ========================= */

    trigger(event, data = {}) {
        if (!this._assertRegistered(event)) return;

        // trigger는 순수 이벤트명만
        if (event.includes('.')) {
            console.warn('[EventBus:trigger] namespace is not allowed in event name');
            return;
        }

        if (this.debug) console.log('[EventBus:trigger]', event, data);

        // ✅ 항상 단일 payload로 전달
        this.bus.trigger(event, [data]);
    }

    /* =========================
     * off
     * ========================= */

    /**
     * ✅ 네임스페이스 전체 제거 (raw namespace를 넣어도 안전)
     * - offNamespace('$j.F.Car')  => 내부적으로 .j-f-car 로 off
     */
    offNamespace(namespace) {
        if (!namespace) return;

        const safe = this._toSafeNamespace(namespace);
        const ns = `.${safe}`;

        if (this.debug) console.log('[EventBus:offNamespace]', ns, '(raw ns:', namespace, ')');
        this.bus.off(ns);
    }

    /**
     * ✅ 특정 이벤트 제거 (namespace 범위 제한 가능)
     * - offEvent('user:login', '$j.F.Car') => user:login.j-f-car 제거
     * - offEvent('user:login') => user:login 전체 제거(주의)
     */
    offEvent(event, namespace = null) {
        if (!this._assertRegistered(event)) return;

        const evt = namespace ? this._withNamespace(event, namespace) : event;

        if (this.debug) console.log('[EventBus:offEvent]', evt);
        this.bus.off(evt);
    }

    offAll() {
        if (this.debug) console.log('[EventBus:offAll]');
        this.bus.off();
    }

    /* =========================
     * debug
     * ========================= */

    list() {
        const events = $._data(this.bus[0], 'events') || {};
        const result = [];

        Object.keys(events).forEach(eventName => {
            events[eventName].forEach(handlerObj => {
                // namespace가 있으면 event.namespace 형태로
                if (handlerObj.namespace) {
                    result.push(`${eventName}.${handlerObj.namespace}`);
                } else {
                    result.push(eventName);
                }
            });
        });

        return result;
    }


}